import { BarChart3 } from "lucide-react";
import type { BlogSnapshotBlock, BlogSnapshotMethodology } from "@shared/blog";

// Relative imports on purpose: vitest.config.ts does not alias "@".
import { formatArabicDate, formatBlockValue } from "../../lib/blog/format";
import { toArabicNumerals } from "../../lib/arabicNumerals";
import { BarChartSvg } from "./BarChartSvg";
import { useBlogPost } from "./BlogPostContext";

const TH_CLASS = "text-start font-bold bg-gray-50 border-b border-gray-200 px-4 py-2";
const TD_CLASS = "text-start border-b border-gray-100 px-4 py-2 align-top";

function BlockTable({ block }: { block: BlogSnapshotBlock }) {
  const hasN = block.rows.some((row) => typeof row.n === "number");
  return (
    <div className="overflow-x-auto mt-4">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr>
            <th scope="col" className={TH_CLASS}>
              البند
            </th>
            <th scope="col" className={TH_CLASS}>
              {block.unit === "percent" ? "النسبة" : "العدد"}
            </th>
            {hasN ? (
              <th scope="col" className={TH_CLASS}>
                عدد المتاجر
              </th>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {block.rows.map((row, index) => (
            <tr key={`${index}-${row.label}`}>
              <td className={TD_CLASS} dir="auto">
                {row.label}
              </td>
              <td className={TD_CLASS}>{formatBlockValue(row.value, block.unit)}</td>
              {hasN ? (
                <td className={TD_CLASS}>
                  {typeof row.n === "number" ? toArabicNumerals(row.n) : ""}
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Methodology({ methodology }: { methodology: BlogSnapshotMethodology }) {
  const keywordGroups = Object.entries(methodology.keywordGroups ?? {});
  return (
    <details className="mt-4">
      <summary className="text-sm font-bold text-warmOrange-700 cursor-pointer min-h-11 flex items-center">
        إزاي حسبنا الأرقام دي؟
      </summary>
      <div className="text-sm leading-[1.6] text-gray-600 space-y-2 mt-2">
        {(methodology.notes ?? []).map((note, index) => (
          <p key={index}>{note}</p>
        ))}
        {methodology.reviewSources?.length ? (
          <p>المصادر المحسوبة: {methodology.reviewSources.join("، ")}.</p>
        ) : null}
        {methodology.excludedSources?.length ? (
          <p>مصادر مش محسوبة: {methodology.excludedSources.join("، ")}.</p>
        ) : null}
        {keywordGroups.length ? (
          <p>
            مجموعات الكلمات: {keywordGroups.map(([group, words]) => `${group} (${words})`).join("؛ ")}.
          </p>
        ) : null}
      </div>
    </details>
  );
}

/**
 * `<DataBlock id />` (D-04, D-06): a mechanical, dated block from the post snapshot. Rows render in
 * the stored order (never sorted: a data block is not a ranking). The methodology disclosure shows
 * on the first DataBlock of the post only, decided by `firstDataBlockId` from the post's +data so
 * SSR and hydration agree. A missing id renders nothing (the build validator is the real guard).
 */
export function DataBlock({ id }: { id: string }) {
  const { snapshot, firstDataBlockId } = useBlogPost();
  const block = snapshot?.blocks?.[id];

  if (!snapshot || !block) {
    console.error(`[blog] DataBlock: unknown snapshot block "${id}"`);
    return null;
  }

  return (
    <figure className="my-8 bg-white border border-gray-200 rounded-xl p-4 md:p-6">
      <div className="flex items-center gap-2">
        <BarChart3 className="h-5 w-5 text-warmOrange-700 shrink-0" aria-hidden="true" />
        <p className="text-xl leading-normal font-bold text-gray-900">{block.title}</p>
      </div>
      {block.note ? <p className="text-sm leading-[1.6] text-gray-600 mt-2">{block.note}</p> : null}
      {block.kind === "bars" ? (
        <BarChartSvg rows={block.rows} unit={block.unit} title={block.title} />
      ) : null}
      <BlockTable block={block} />
      <figcaption className="text-sm leading-[1.6] text-gray-500 mt-4 border-t border-gray-100 pt-4">
        {`حتى تاريخ ${formatArabicDate(snapshot.asOf)}. الأرقام محسوبة من تقييمات منشورة على يتفرج.`}
      </figcaption>
      {id === firstDataBlockId ? <Methodology methodology={snapshot.methodology} /> : null}
    </figure>
  );
}

export default DataBlock;
